import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'

import useEffectCompare from 'src/common/hooks/helpers/useEffectCompare'
import { OwnedLockedToken } from 'src/common/types/models/Tokens'
import { LockedAmountsInOTCQuery } from '../queries/swarmx/LockedAmountInOTCQuery'
import {
  LockedAmountsInOtcQuery as LockedAmountQueryGQL,
  LockedAmountsInOtcQueryVariables,
  OfferStatus,
} from '../types/swarmx/graphql'
import { normalizeAllLockedAmounts } from '../utils/dotc'

export const PAGE_LIMIT = 1000

const useLockedAmountsInOTC = (
  options?: QueryHookOptions<
    LockedAmountQueryGQL,
    LockedAmountsInOtcQueryVariables
  >
) => {
  const [hasMore, setHasMore] = useState(true)
  const [limit, setLimit] = useState(PAGE_LIMIT)

  const filter = useMemo(
    () => ({
      ...options?.variables?.filter,
      offerStatus_not_in: [OfferStatus.Cancelled, OfferStatus.FullyTaken],
    }),
    [options?.variables?.filter]
  )
  useEffectCompare(() => setHasMore(true), [filter])

  const {
    data,
    loading,
    error,
    fetchMore: nativeFetchMore,
    networkStatus,
    refetch,
  } = useQuery(LockedAmountsInOTCQuery, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...options,
    variables: {
      limit: limit,
      skip: 0,
      ...options?.variables,
      filter: filter,
    },
  })

  const lockedAmounts = useMemo(() => {
    return normalizeAllLockedAmounts(data?.offers || [])
  }, [data?.offers])

  const tokensWithLockedAmountDictionary = useMemo(() => {
    const tokensDictionary = new Map<string, OwnedLockedToken>()

    lockedAmounts.forEach(({ lockedToken, lockedAmount }) => {
      if (tokensDictionary.has(lockedToken.id)) {
        const previousAmount = tokensDictionary.get(
          lockedToken.id
        )!.lockedAmount
        const newAmount = previousAmount.plus(lockedAmount)
        tokensDictionary.set(lockedToken.id, {
          ...lockedToken,
          lockedAmount: newAmount,
        })
      } else {
        tokensDictionary.set(lockedToken.id, {
          ...lockedToken,
          lockedAmount,
        })
      }
    })

    return tokensDictionary
  }, [lockedAmounts])

  const tokensWithLockedAmount = Array.from(
    tokensWithLockedAmountDictionary.values()
  )

  const fetchMore = useCallback(async () => {
    if (options?.skip || !hasMore || error) return null
    const currentLength = lockedAmounts.length

    const offersResponse = await nativeFetchMore({
      variables: {
        ...options?.variables,
        skip: currentLength,
        limit: PAGE_LIMIT,
        filter: filter,
      },
    })

    if (offersResponse.data.offers.length < PAGE_LIMIT) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }

    setLimit(currentLength + offersResponse.data.offers.length)

    return offersResponse
  }, [
    error,
    filter,
    hasMore,
    lockedAmounts.length,
    nativeFetchMore,
    options?.skip,
    options?.variables,
  ])

  return useMemo(
    () => ({
      lockedAmounts,
      tokensWithLockedAmount,
      tokensWithLockedAmountDictionary,
      hasMore,
      loading:
        loading ||
        ![
          NetworkStatus.ready,
          NetworkStatus.error,
          NetworkStatus.poll,
        ].includes(networkStatus),
      error,
      refetch,
      refetching: [NetworkStatus.poll].includes(networkStatus),
      fetchMore: fetchMore,
    }),
    [
      error,
      fetchMore,
      hasMore,
      loading,
      lockedAmounts,
      networkStatus,
      refetch,
      tokensWithLockedAmount,
      tokensWithLockedAmountDictionary,
    ]
  )
}

export default useLockedAmountsInOTC
